import React from 'react';
import { BarChart, Trash2, Layers } from 'lucide-react';
import { formatCurrency, formatNumber } from '../../../../../utils/formatting';
import Button from '../../../../../components/ui/Button';

/**
 * Компонент боковой панели для сравнения сохраненных инвестиционных планов
 */
const ComparisonSidebar = ({ 
  savedInvestments, 
  deleteSavedInvestment, 
  loadSavedInvestment 
}) => {
  // Функция для склонения слов
  const pluralize = (number, one, few, many) => {
    let n = Math.abs(number);
    n %= 100;
    if (n >= 5 && n <= 20) {
      return many;
    }
    n %= 10;
    if (n === 1) {
      return one;
    }
    if (n >= 2 && n <= 4) {
      return few;
    }
    return many;
  };

  // Если нет сохраненных инвестиционных планов
  if (!savedInvestments || savedInvestments.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
          <BarChart className="w-5 h-5 mr-2 text-indigo-500" />
          Сравнение инвестиционных планов
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Здесь будут отображаться сохраненные инвестиционные планы для сравнения.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white flex items-center">
          <BarChart className="w-5 h-5 mr-2 text-indigo-500" />
          Сравнение инвестиционных планов
        </h3>
        <span className="text-sm text-gray-500 dark:text-gray-400">
          {savedInvestments.length} {pluralize(savedInvestments.length, 'план', 'плана', 'планов')}
        </span>
      </div>

      <div className="space-y-4">
        {savedInvestments.map((investment) => (
          <div 
            key={investment.id} 
            className="bg-gray-50 dark:bg-gray-700/30 border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors group"
          >
            <div className="flex justify-between items-start mb-2">
              <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                {investment.name}
              </h4>
              <div className="flex items-center space-x-2">
                <Button
                  size="sm"
                  variant="ghost"
                  icon={<Layers className="w-4 h-4" />}
                  onClick={() => loadSavedInvestment(investment.id)}
                  title="Загрузить план"
                />
                <Button
                  size="sm"
                  variant="ghost"
                  icon={<Trash2 className="w-4 h-4 text-red-500" />}
                  onClick={() => deleteSavedInvestment(investment.id)}
                  title="Удалить"
                />
              </div>
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">
              <div className="flex justify-between mb-1">
                <span>Начальная сумма:</span>
                <span className="font-medium">
                  {formatCurrency(investment.formData.initialInvestment, 0)}
                </span>
              </div>
              <div className="flex justify-between mb-1">
                <span>Срок:</span>
                <span>
                  {investment.formData.investmentTerm} {pluralize(parseFloat(investment.formData.investmentTerm), 'год', 'года', 'лет')}
                </span>
              </div>
              <div className="flex justify-between mb-1">
                <span>Ежемесячный взнос:</span>
                <span>
                  {formatCurrency(investment.formData.monthlyContribution, 0)}
                </span>
              </div>
              <div className="flex justify-between mb-1">
                <span>Риск:</span>
                <span className={`font-semibold ${
                  investment.formData.riskLevel === 'low' ? 'text-green-600' : 
                  investment.formData.riskLevel === 'moderate' ? 'text-yellow-600' : 
                  'text-red-600'
                }`}>
                  {investment.formData.riskLevel === 'low' ? 'Низкий' : 
                   investment.formData.riskLevel === 'moderate' ? 'Умеренный' : 
                   'Высокий'}
                </span>
              </div>
              <div className="flex justify-between">
                <span>Итоговый баланс:</span>
                <span className="font-semibold text-indigo-600 dark:text-indigo-400">
                  {formatCurrency(investment.results.finalBalance, 0)}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ComparisonSidebar;